import { TsbFinancialFilterChangedProperties } from 'core/ampli';
import {
  AMPLITUDE_REPORT_TYPE,
  FINANCIAL_FILTERS,
  useAmplitude,
} from 'hooks/amplitude';
import { useState } from 'react';

// These maps will define the relation between `keyof FinancialFilters` and the enum name
// in the amplitude event for the different tables.
const depositFilterKeyToAmplitude: Map<string, FINANCIAL_FILTERS> = new Map([
  ['depositAccount', FINANCIAL_FILTERS.DEPOSIT_ACCOUNT],
]);

const outstandingFflterKeyToAmplitude: Map<string, FINANCIAL_FILTERS> = new Map(
  [
    ['program', FINANCIAL_FILTERS.PROGRAM],
    ['sourceType', FINANCIAL_FILTERS.SOURCE_TYPE],
    ['registration', FINANCIAL_FILTERS.REGISTRATION],
    ['invoice', FINANCIAL_FILTERS.INVOICE],
    ['status', FINANCIAL_FILTERS.STATUS],
  ]
);

const transactionFilterKeyToAmplitude: Map<string, FINANCIAL_FILTERS> = new Map(
  [
    ['program', FINANCIAL_FILTERS.PROGRAM],
    ['sourceType', FINANCIAL_FILTERS.SOURCE_TYPE],
    ['registration', FINANCIAL_FILTERS.REGISTRATION],
    ['invoice', FINANCIAL_FILTERS.INVOICE],
    ['paymentType', FINANCIAL_FILTERS.PAYMENT_TYPE],
    ['depositAccount', FINANCIAL_FILTERS.DEPOSIT_ACCOUNT],
    ['status', FINANCIAL_FILTERS.STATUS],
  ]
);

const useAmplitudeFilter = (
  reportType: AMPLITUDE_REPORT_TYPE,
  filterKeyToAmplitude: Map<string, string>
) => {
  const { amplitudeTrack } = useAmplitude();
  const [lastFilter, setLastFilter] = useState('');
  const [firedLatch, setFiredLatch] = useState(false);
  const [onClearLatch, setOnClearLatch] = useState(true);

  return {
    prepareFilterForAmplitude: (filter: string) => {
      setLastFilter(filter);
      setFiredLatch(false);
    },
    trackResultsUsingLastFilterForAmplitude: (rowLength: number) => {
      // lastFilter !== '' keeps the initial load from firing
      if (
        lastFilter !== '' &&
        filterKeyToAmplitude.has(lastFilter) &&
        firedLatch === false
      ) {
        const financialFilter = filterKeyToAmplitude.get(lastFilter);
        amplitudeTrack.tsbFinancialFilterChanged({
          financial_filter: financialFilter,
          financial_report: reportType,
          page_result_count: rowLength,
        } as TsbFinancialFilterChangedProperties);
        setFiredLatch(true);
      }

      if (onClearLatch === false) {
        amplitudeTrack.tsbFinancialFilterChanged({
          financial_filter: FINANCIAL_FILTERS.CLEAR_ALL,
          financial_report: reportType,
          page_result_count: rowLength,
        });
        setOnClearLatch(true);
      }
    },
    trackOnClearForAmplitude: () => {
      setLastFilter('');
      setFiredLatch(false);
      setOnClearLatch(false);
    },
  };
};

/**
 * This function takes a report type and returns three functions.
 *
 * prepareFilterForAmplitude: This function needs to be run whenever a new filter is being set.
 *   Normally would be a part of the `setFilter` method.
 *
 * trackResultsUsingLastFilterForAmplitude: This function is called in the component after the
 *   data has been filtered down to just the matching rows. It does not need to be in a hook.
 *
 * trackOnClearForAmplitude: This is a function that needs to be called to tell the hook that
 *   you're about to clear all filters. This tells the hook that the next filter state change
 *   should be reported as a CLEAR_ALL event type.
 *
 *   NOTE: In order for this to work all filters must be updated in one state change.
 */
export const useAmplitudeFilterTracking = (report: AMPLITUDE_REPORT_TYPE) => {
  let reportType: AMPLITUDE_REPORT_TYPE;
  let filterKeyToAmplitude: Map<string, string>;

  switch (report) {
    case AMPLITUDE_REPORT_TYPE.DEPOSIT:
      reportType = AMPLITUDE_REPORT_TYPE.DEPOSIT;
      filterKeyToAmplitude = depositFilterKeyToAmplitude;
      break;
    case AMPLITUDE_REPORT_TYPE.OUTSTANDING:
      reportType = AMPLITUDE_REPORT_TYPE.OUTSTANDING;
      filterKeyToAmplitude = outstandingFflterKeyToAmplitude;
      break;
    case AMPLITUDE_REPORT_TYPE.TRANSACTIONS:
      reportType = AMPLITUDE_REPORT_TYPE.TRANSACTIONS;
      filterKeyToAmplitude = transactionFilterKeyToAmplitude;
      break;
  }

  return useAmplitudeFilter(reportType, filterKeyToAmplitude);
};
